import { useEffect } from "react";

import ScrollTop from "../features/ScrollTop";

function MainPrivacyPolicy() {
  useEffect(() => {
    document.title = "Privacy Policy | AttnQ";
  }, []);

  return (
    <main className="text-gray-800 px-8 py-20 shadow">
      <ScrollTop />
      <div className="max-w-5xl mx-auto space-y-10">
        {/* Page Heading */}
        <header className="text-center space-y-2">
          <h1 className="text-xl md:text-3xl font-bold">Privacy&nbsp;Policy</h1>
          <p className="text-sm text-gray-500">
            Updated on and effective from:{" "}
            <span className="italic">[02&nbsp;06&nbsp;2024]</span>
          </p>
        </header>

        {/* Sections */}
        <article className="space-y-8">
          {/* 1. Who we are */}
          <section>
            <h2 className="text-xl md:text-2xl font-semibold my-2">
              1. Who we are
            </h2>
            <p className="text-sm text-gray-600">
              AttnQ (also pronounced <strong>Attain Queue</strong>) (“
              <strong>we</strong>,” “<strong>our</strong>,” “<strong>us</strong>
              ”) is a property-matching platform that connects buyers/investors
              with vetted real-estate brokers. We operate the website
              <strong>&nbsp;attnq.com</strong> and related services
              (collectively, the “<strong>Platform</strong>”). <br />
              <br />
              The web domain <strong>attnq.com</strong> and all email addresses
              ending with
              <strong> @attnq.com</strong> are the official digital properties
              of AttnQ; any communication or interaction originating from these
              domains should be considered as authorized and representative of
              the AttnQ company.
            </p>
          </section>

          {/* 2. Scope */}
          <section>
            <h2 className="text-xl md:text-2xl font-semibold my-2">2. Scope</h2>
            <p className="text-sm text-gray-600">
              This Policy explains how we collect, use, disclose, and safeguard
              your information when you browse, submit property requirements, or
              access buyer leads through the Platform.
            </p>
          </section>

          {/* 3. Information we collect */}
          <section>
            <h2 className="text-xl md:text-2xl font-semibold my-2">
              3. Information we collect
            </h2>

            <div className="overflow-x-auto">
              <table className="w-full text-left border border-gray-200 text-sm">
                <thead className="bg-yellow-50">
                  <tr>
                    <th className="p-3 font-medium">Category</th>
                    <th className="p-3 font-medium">Examples</th>
                    <th className="p-3 font-medium">Source</th>
                  </tr>
                </thead>
                <tbody className="text-sm text-gray-600">
                  <tr className="border-t">
                    <td className="p-3 font-medium">Identity</td>
                    <td className="p-3">Name, Company, Location</td>
                    <td className="p-3">You</td>
                  </tr>
                  <tr className="border-t">
                    <td className="p-3 font-medium">Contact</td>
                    <td className="p-3">Email, Phone</td>
                    <td className="p-3">You</td>
                  </tr>
                  <tr className="border-t">
                    <td className="p-3 font-medium">
                      Property&nbsp;Preferences
                    </td>
                    <td className="p-3">
                      Budget, Location, Space, Amenities, Plot type, Project
                      stage, Deal-breakers, Buying intent
                    </td>
                    <td className="p-3">You</td>
                  </tr>
                  <tr className="border-t">
                    <td className="p-3 font-medium">
                      Usage&nbsp;&amp;&nbsp;Device
                    </td>
                    <td className="p-3">
                      IP, Browser, Device ID, Device OS, Pages viewed, Time
                      viewed
                    </td>
                    <td className="p-3">Cookies / analytics</td>
                  </tr>
                  <tr className="border-t">
                    <td className="p-3 font-medium">Payment</td>
                    <td className="p-3">Masked card details, UPI ref-IDs</td>
                    <td className="p-3">Payment partner</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="text-sm text-gray-600 mt-6">
              *We do <strong>not</strong> knowingly collect data from children
              under&nbsp;18.
            </p>
          </section>

          {/* 4. How we use your information */}
          <section>
            <h2 className="text-xl md:text-2xl font-semibold my-2">
              4. How we use your information
            </h2>
            <ol className="list-decimal list-inside space-y-1 text-sm text-gray-600">
              <li>
                Matchmaking – Queue and score buyer requirements for relevant
                brokers.
              </li>
              <li>
                Service delivery – Create and manage your account, send
                confirmations/OTP.
              </li>
              <li>Communication – Emails, calls, SMS</li>
              <li>
                Improvement &amp; analytics – Diagnose errors, test new
                features, measure demand.
              </li>
            </ol>
          </section>

          {/* 5. Legal bases */}
          <section>
            <h2 className="text-xl md:text-2xl font-semibold my-2">
              5. Legal bases&nbsp;(GDPR / DPDP Act)
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-left border border-gray-200 text-sm">
                <thead className="bg-yellow-50">
                  <tr>
                    <th className="p-3 font-medium">Purpose</th>
                    <th className="p-3 font-medium">Legal basis</th>
                  </tr>
                </thead>
                <tbody className="text-sm text-gray-600">
                  <tr className="border-t">
                    <td className="p-3">Account creation &amp; matchmaking</td>
                    <td className="p-3">Contractual necessity</td>
                  </tr>
                  <tr className="border-t">
                    <td className="p-3">Marketing emails (opt-in)</td>
                    <td className="p-3">Consent</td>
                  </tr>
                  <tr className="border-t">
                    <td className="p-3">Service analytics</td>
                    <td className="p-3">Legitimate interest</td>
                  </tr>
                  <tr className="border-t">
                    <td className="p-3">Regulatory disclosures</td>
                    <td className="p-3">Legal obligation</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* 6. Sharing & disclosure */}
          <section>
            <h2 className="text-xl md:text-2xl font-semibold my-2">
              6. Sharing &amp; disclosure
            </h2>
            <p className="text-sm text-gray-600">
              Data is shared only when necessary — with vetted brokers. We never
              sell personal data. Company and it's operational data won't be
              shared with non-government or private entities. Employees and
              their project specific data won't be shared with non-government or
              private entities. <br />
              <br />
              Your data is shared only when necessary to provide our
              services—for example, when presenting buyer requirements to
              verified brokers, or when using trusted third-party tools for
              infrastructure, analytics, or communication. All such partners are
              bound by strict confidentiality and data protection agreements.
            </p>
          </section>

          {/* 7–14 (condensed for brevity) */}
          <section className="space-y-4">
            <div className="my-8">
              <h2 className="text-xl md:text-2xl font-semibold">
                7. International transfers
              </h2>
              <p className="text-sm text-gray-600">
                Data may be processed outside your country (e.g., AWS Singapore)
                with adequate safeguards.
              </p>
            </div>

            <div className="my-8">
              <h2 className="text-xl md:text-2xl font-semibold my-2">
                8. Data retention
              </h2>
              <p className="text-sm text-gray-600">
                We retain personal data only for as long as it’s necessary to
                deliver our services effectively, comply with legal obligations,
                and maintain platform integrity. <br />
                <br />
                Buyer information is kept while their account remains active or
                until a prolonged period of inactivity, after which it may be
                deleted or anonymized. Broker-related data is retained as long
                as the account is in use, and may be purged within a reasonable
                period after closure. Certain non-personal or aggregated data
                may be stored indefinitely for analytics, system improvement, or
                reporting purposes.
              </p>
            </div>

            <div className="my-8">
              <h2 className="text-xl md:text-2xl font-semibold my-2">
                9. Security
              </h2>
              <p className="text-sm text-gray-600">
                Encryption in transit (TLS 1.2+), at rest (AES-256), role-based
                access, and regular security testing. <br />
                <br />
                We are committed to protecting your personal information with
                robust security practices across all levels of our platform. All
                data transmitted between you and our servers is encrypted using
                modern TLS protocols, and sensitive information is securely
                stored using encryption at rest. Access to personal data is
                restricted to authorized personnel only, and is granted based on
                the principle of least privilege to ensure that only those with
                a clear operational need can access user information. <br />
                <br />
                Our infrastructure is hosted on trusted, industry-leading cloud
                service providers that implement strong physical and network
                safeguards, including firewalls, intrusion detection, and
                automated patching of known vulnerabilities. We monitor our
                systems for suspicious activity and actively work to prevent
                unauthorized access or misuse. User authentication is protected
                with secure password handling, and we are working toward
                optional support for two-factor authentication to enhance
                account security for brokers and teams. We also routinely audit
                our code, infrastructure, and internal processes to identify and
                fix potential vulnerabilities, while ensuring that all employees
                handling data are trained in data privacy and security best
                practices. <br />
                <br />
                In the unlikely event of a data breach, we have a response plan
                in place to investigate, contain, and notify affected users
                without undue delay, and within legal timeframes where
                applicable. We continuously evaluate and improve our security
                measures to stay ahead of evolving threats and to keep your data
                safe as the platform grows.
              </p>
            </div>

            <div className="my-8">
              <h2 className="text-xl md:text-2xl font-semibold my-2">
                10. Third-party links
              </h2>
              <p className="text-sm text-gray-600">
                Our platform may contain links to third-party websites, tools,
                or services that are not operated or controlled by AttnQ. We are
                not responsible for the privacy practices or content of these
                external sites. Once you leave our platform and interact with a
                third-party service, their terms and privacy policies apply. We
                recommend reviewing those policies before providing any personal
                information or engaging with their services.
              </p>
            </div>

            <div className="my-8">
              <h2 className="text-xl md:text-2xl font-semibold my-2">
                11. Changes to this Policy
              </h2>
              <p className="text-sm text-gray-600">
                We may update this Privacy Policy from time to time to reflect
                changes in our services, legal requirements, or internal
                practices, and we encourage you to review it periodically.
                Whenever a material revision is made, we will post the new
                version on our website and, where feasible, provide additional
                notice—such as sending an email or displaying an in-app alert—so
                that you remain informed about how your data is handled. Because
                our product evolves quickly, we reserve the right to amend or
                replace the policy without prior notice when immediate changes
                are necessary to protect user data, comply with urgent legal
                directives, or address emerging security concerns. Your
                continued use of AttnQ after any modification constitutes
                acceptance of the revised terms; if you do not agree, you should
                discontinue use of the platform.
              </p>
            </div>
          </section>
        </article>
      </div>
    </main>
  );
}

export default MainPrivacyPolicy;
